import React, { useState, useEffect } from 'react';
import styles from './RegistrationForm.module.css';
import { getBrowserLanguage } from '../../utils/browserUtils';
import { useTheme } from '../../context/ThemeContext';
import { useRegistration } from '../../hooks/useRegistration';

const RegistrationForm = ({ onRegistered, inviteCode, linkingParams }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const [languageCode, setLanguageCode] = useState('en');
  const [countryCode, setCountryCode] = useState('US'); // Re-add needed state
  const [colorPalette, setColorPalette] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [gender, setGender] = useState('');
  const [nickname, setNickname] = useState('');
  const { registrationData, loading, error, fetchRegistrationData, register, link } = useRegistration();

  // Tab and linking states
  const [activeTab, setActiveTab] = useState(linkingParams ? 'link' : 'register');
  const [userKey, setUserKey] = useState(linkingParams?.userKey || '');
  const [linkingCode, setLinkingCode] = useState(linkingParams?.linkingCode || '');


  // Pick form defaults from freshly loaded registration data
  const applyDefaults = (data) => {
    // Set default theme if not already set
    if (!colorPalette && data.themes && data.themes.length > 0) {
      setColorPalette(data.themes[0].code);
    }
    
    // Set default nickname to first available option
    if (data.nicknames && data.nicknames.length > 0) {
      setNickname(data.nicknames[0]);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const language = getBrowserLanguage();
    setLanguageCode(language); // Set initial language from browser
    fetchRegistrationData(language, (data) => {
      // the browser locale as one of the server's codes (unknown → 'en')
      const codes = (data.languages || []).map(lang => lang.code);
      setLanguageCode(getBrowserLanguage(codes));
      applyDefaults(data);
    });
  }, []);

  // Update form when linkingParams change
  useEffect(() => {
    if (linkingParams) {
      setActiveTab('link');
      setUserKey(linkingParams.userKey || '');
      setLinkingCode(linkingParams.linkingCode || '');
    }
  }, [linkingParams]);
 

  // Handle language change
  const handleLanguageChange = (e) => {
    const newLanguage = e.target.value;
    setLanguageCode(newLanguage);
    // Re-fetch registration data with new language
    fetchRegistrationData(newLanguage, applyDefaults);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    await register(
      { countryCode, birthYear, gender, nickname, languageCode, colorPalette, isDarkMode, inviteCode },
      onRegistered
    );
  };

  // Handle link submission
  const handleLink = async (e) => {
    e.preventDefault();
    await link({ userKey, linkingCode }, onRegistered);
  };

  // Get text from registration data
  const getText = (key) => {
    const keys = key.split('.');
    let value = registrationData;
    
    for (const k of keys) {
      if (!value || typeof value !== 'object') {
        return key; // Return key if path doesn't exist
      }
      value = value[k];
    }
    
    return value || key; // Return key as fallback if value is null/undefined
  };

  // Generate birth year options (current year - 100 to current year - 5)
  const generateBirthYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 5; year >= currentYear - 100; year--) {
      years.push(year);
    }
    return years;
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!registrationData) return <div className="error">Failed to load registration data</div>;

  // Check if Link button should be disabled
  const isLinkDisabled = !userKey.trim() || !linkingCode.trim() || loading;

  return (
    <div className={styles.formContainer}>
      <h2>Welcome to GridRank</h2>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'register' ? styles.active : ''}`}
          onClick={() => setActiveTab('register')}
        >
          Register
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'link' ? styles.active : ''}`}
          onClick={() => setActiveTab('link')}
        >
          Link
        </button>
      </div>

      {inviteCode && (
        <div className={styles.inviteNotice}>
          {getText('registration.invited_notice', 'You were invited by a friend!')}
        </div>
      )}

      {/* Registration Form */}
      {activeTab === 'register' && (
        <form onSubmit={handleSubmit}>
        {/* Hidden field for invite code */}
        {inviteCode && (
          <input type="hidden" name="invited_by" value={inviteCode} />
        )}

        <div className={styles.formGroup}>
          <label htmlFor="languageCode">
            {getText('common_text.language')}
          </label>
          <select 
            id="languageCode" 
            value={languageCode} 
            onChange={handleLanguageChange}
            disabled={loading}
          >
            {registrationData?.languages?.map(lang => (
              <option key={lang.code} value={lang.code}>{lang.name}</option>
            ))}
          </select>
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="countryCode">
            {getText('common_text.country')} <span className={styles.labelDescription}>- {getText('registration.country_description')}</span>
          </label>
          <input 
            type="text" 
            id="countryCode" 
            value={countryCode} 
            onChange={(e) => setCountryCode(e.target.value)}
            disabled={loading}
          />
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="birthYear">
            {getText('common_text.birth_year')} <span className={styles.labelDescription}>- {getText('registration.birth_year_description')}</span>
          </label>
          <select
            id="birthYear"
            value={birthYear}
            onChange={(e) => setBirthYear(e.target.value)}
            disabled={loading}
          >
            <option value="">--{getText('common_text.optional_value_default')}--</option>
            {generateBirthYearOptions().map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="gender">
            {getText('common_text.gender')} <span className={styles.labelDescription}>- {getText('registration.gender_description')}</span>
          </label>
          <select
            id="gender"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            disabled={loading}
          >
            <option value="">--{getText('common_text.optional_value_default')}--</option>
            {registrationData?.genders?.map(g => (
              <option key={g.code} value={g.code}>{g.name}</option>
            ))}
          </select>
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="nickname">
            {getText('common_text.nickname')}
          </label>
          <select
            id="nickname"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            disabled={loading}
          >
            {registrationData?.nicknames?.map(nick => (
              <option key={nick} value={nick}>{nick}</option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="colorPalette">
            {getText('common_text.theme')}
          </label>
          <select 
            id="colorPalette" 
            value={colorPalette} 
            onChange={(e) => setColorPalette(e.target.value)}
            disabled={loading}
          >
            <option value="">--Select theme--</option>
            {registrationData?.themes?.map(theme => (
              <option key={theme.code} value={theme.code}>{theme.name}</option>
            ))}
          </select>
        </div>
        
        <div className={styles.formGroup}>
          <label>
            {getText('common_text.light_mode')}
          </label>
          <div className={styles.toggleContainer}>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={isDarkMode}
                onChange={toggleTheme}
                disabled={loading}
              />
              <span className={styles.toggleSlider}></span>
            </label>
            <span className={styles.toggleLabel}>
              {isDarkMode ? 'Dark' : 'Light'}
            </span>
          </div>
        </div>
        
          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>
      )}

      {/* Linking Form */}
      {activeTab === 'link' && (
        <form onSubmit={handleLink}>
          <div className={styles.linkInstructions}>
            <p><strong>Already using GridRank on another device?</strong></p>
            <p>You can link this device to that account.</p>
            <p>On the other device go to your User Profile and get the User Key and Linking Code.</p>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="userKey">User Key</label>
            <input
              type="text"
              id="userKey"
              value={userKey}
              onChange={(e) => setUserKey(e.target.value.toUpperCase())}
              placeholder="Enter User Key"
              disabled={loading}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="linkingCode">Linking Code</label>
            <input
              type="text"
              id="linkingCode"
              value={linkingCode}
              onChange={(e) => setLinkingCode(e.target.value.toUpperCase())}
              placeholder="Enter Linking Code"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isLinkDisabled}
          >
            {loading ? 'Linking...' : 'Link'}
          </button>

          <div className={styles.orDivider}>OR</div>

          <div className={styles.linkInstructions}>
            <p>On your other device use the <strong>*Link via QR Code*</strong> option and scan the QR code with this device.</p>
          </div>
        </form>
      )}

      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

export default RegistrationForm;
